/*
 * @Description: 地图点位 — 添加 / 编辑点位表单（对齐设计图 图1/图2）
 *   名称 + 图标选择(4 预设 + 自定义上传) + 位置(高德搜索/地图点选 → 反解地址 + 经纬度) + 保存。
 *   产品确认：图标直接存图片 url，不存类型/围栏；地址由前端高德解析。
 */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Input, InputNumber, AutoComplete, Upload, message } from 'antd'
import {
  EnvironmentOutlined,
  LoadingOutlined,
  PlusOutlined
} from '@ant-design/icons'
import AMapLoader from '@amap/amap-jsapi-loader'
import CONFIG from '../../../../constants/config'
import cos from '../../../../utils/cos'
import { geoRegeo, savePoi } from '../../../../actions/locationCockpit'
import { useAppDispatch } from '../../../../app/hooks'
import {
  isCockpitMockEnabled,
  MAP_FEATURES,
  POI_ICON_TYPES
} from '../../constants'
import { useGeoSearch } from '../../hooks/useGeoSearch'
import { MOCK_REGEO } from '../../mock/geo.mock'
import { CockpitEnvelope } from '../../types/cockpit.types'
import { PoiItem, PoiSaveData, RegeoResult } from '../../types/poi.types'

const COS_BUCKET = 'net-cloud-1322834237'
const COS_REGION = 'ap-guangzhou'
// 腾讯云 COS（桶 net-cloud-1322834237）对应的访问域名是 net-cloud-tx.xhey.top（-tx）。
// 不能用 net-cloud.xhey.top（那是阿里云 OSS 的域名），否则图片上传成功也访问不到。
const COS_URL_PREFIX = 'https://net-cloud-tx.xhey.top/'
const PREVIEW_ID = 'poi-editor-preview-map'
const NAME_MAX = 20

interface PoiEditorProps {
  groupId: string
  /** 编辑的点位；null = 新增 */
  editing: PoiItem | null
  /** 取消（返回列表） */
  onCancel: () => void
  /** 保存成功（回传保存点位的坐标，用于定位地图） */
  onSaved: (coord: { lng: number; lat: number }) => void
}

/** 上传图标到 COS，返回公网 url */
function uploadIconToCos(file: File): Promise<string> {
  const ext = (file.name.split('.').pop() || 'png').toLowerCase()
  const uuid = 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
  const key = `group/poi-icon/${uuid}.${ext}`
  return new Promise((resolve, reject) => {
    cos.uploadFile(
      {
        Bucket: COS_BUCKET,
        Region: COS_REGION,
        Key: key,
        Body: file,
        SliceSize: 1024 * 1024 * 30
      },
      (err: unknown) => {
        if (err) reject(err)
        else resolve(COS_URL_PREFIX + key)
      }
    )
  })
}

/** 小地图预览：在选中坐标放一个 marker，支持点地图改坐标 */
const PoiPreviewMap: React.FC<{
  lng?: number
  lat?: number
  onPick: (lng: number, lat: number) => void
}> = ({ lng, lat, onPick }) => {
  const mapRef = useRef<any>(null)
  const markerRef = useRef<any>(null)
  const onPickRef = useRef(onPick)
  onPickRef.current = onPick
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let destroyed = false
    AMapLoader.load({ key: CONFIG.GAODE_KEY, version: '2.0' })
      .then((AMap) => {
        if (destroyed) return
        window.AMap = AMap
        const map = new AMap.Map(PREVIEW_ID, {
          zoom: 15,
          mapStyle: 'amap://styles/whitesmoke',
          viewMode: '2D',
          features: MAP_FEATURES
        })
        map.on('click', (e: any) => {
          onPickRef.current(e.lnglat.getLng(), e.lnglat.getLat())
        })
        mapRef.current = map
        setReady(true)
      })
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      .catch(() => {})
    return () => {
      destroyed = true
      if (mapRef.current) {
        mapRef.current.destroy()
        mapRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (!ready || !mapRef.current || !window.AMap) return
    if (lng == null || lat == null) {
      if (markerRef.current) {
        mapRef.current.remove(markerRef.current)
        markerRef.current = null
      }
      return
    }
    const pos: [number, number] = [lng, lat]
    if (!markerRef.current) {
      markerRef.current = new window.AMap.Marker({
        position: pos,
        anchor: 'bottom-center'
      })
      mapRef.current.add(markerRef.current)
    } else {
      markerRef.current.setPosition(pos)
    }
    mapRef.current.setCenter(pos)
  }, [ready, lng, lat])

  return <div id={PREVIEW_ID} className="poi-editor__map" />
}

const PoiEditor: React.FC<PoiEditorProps> = ({
  groupId,
  editing,
  onCancel,
  onSaved
}) => {
  const dispatch = useAppDispatch()
  const isMock = isCockpitMockEnabled()
  const isEdit = !!editing

  const [name, setName] = useState(editing ? editing.name : '')
  const [iconUrl, setIconUrl] = useState(
    editing ? editing.iconUrl : POI_ICON_TYPES[0].iconUrl
  )
  const [address, setAddress] = useState(editing ? editing.address : '')
  const [lng, setLng] = useState<number | undefined>(editing?.lng)
  const [lat, setLat] = useState<number | undefined>(editing?.lat)
  const [searchText, setSearchText] = useState(editing ? editing.address : '')
  const [uploading, setUploading] = useState(false)
  const [saving, setSaving] = useState(false)

  const { results, search } = useGeoSearch()

  const presetMatch = POI_ICON_TYPES.find((p) => p.iconUrl === iconUrl)
  const isCustomIcon = !!iconUrl && !presetMatch

  /** 反解地址（前端解析），写入 address */
  const fillAddress = useCallback(
    (lngV: number, latV: number) => {
      if (isMock) {
        setAddress(MOCK_REGEO.address)
        return
      }
      dispatch(geoRegeo({ location: `${lngV},${latV}` }))
        .then((res: CockpitEnvelope<RegeoResult>) => {
          if (res && res.code === 200 && res.data) setAddress(res.data.address)
        })
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        .catch(() => {})
    },
    [dispatch, isMock]
  )

  /** 选中搜索结果 */
  const handleSelectResult = useCallback(
    (idx: string) => {
      const item = results[Number(idx)]
      if (!item) return
      setLng(item.lng)
      setLat(item.lat)
      setSearchText(item.name)
      setAddress(item.name)
      fillAddress(item.lng, item.lat)
    },
    [results, fillAddress]
  )

  /** 地图点选改坐标 */
  const handlePickOnMap = useCallback(
    (lngV: number, latV: number) => {
      const fixedLng = Number(lngV.toFixed(6))
      const fixedLat = Number(latV.toFixed(6))
      setLng(fixedLng)
      setLat(fixedLat)
      fillAddress(fixedLng, fixedLat)
    },
    [fillAddress]
  )

  const handleUpload = useCallback((file: File) => {
    setUploading(true)
    uploadIconToCos(file)
      .then((url) => {
        setIconUrl(url)
        setUploading(false)
      })
      .catch(() => {
        setUploading(false)
        message.error('图标上传失败')
      })
    return false
  }, [])

  const handleSave = useCallback(() => {
    const trimmed = name.trim()
    if (!trimmed) {
      message.warning('请输入点位名称')
      return
    }
    if (trimmed.length > NAME_MAX) {
      message.warning(`点位名称不超过 ${NAME_MAX} 字`)
      return
    }
    if (lng == null || lat == null) {
      message.warning('请选择点位位置')
      return
    }
    if (isMock) {
      message.success(
        isEdit ? '已保存（mock 不持久）' : '已添加（mock 不持久）'
      )
      onSaved({ lng, lat })
      return
    }
    setSaving(true)
    dispatch(
      savePoi({
        groupId,
        id: editing ? editing.id : 0,
        name: trimmed,
        iconUrl,
        address,
        lng,
        lat
      })
    )
      .then((res: CockpitEnvelope<PoiSaveData>) => {
        setSaving(false)
        if (res && res.code === 200) {
          message.success('保存成功')
          onSaved({ lng, lat })
        } else {
          message.error((res && res.toast_msg) || '保存失败')
        }
      })
      .catch(() => {
        setSaving(false)
        message.error('保存失败')
      })
  }, [
    name,
    lng,
    lat,
    iconUrl,
    address,
    editing,
    groupId,
    isMock,
    isEdit,
    dispatch,
    onSaved
  ])

  return (
    <div className="poi-editor">
      <div className="poi-editor__field">
        <label className="poi-editor__label poi-editor__label--required">
          点位名称
        </label>
        <Input
          value={name}
          maxLength={NAME_MAX}
          placeholder={'输入点位名称，如“天府总部大厦”'}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className="poi-editor__field">
        <label className="poi-editor__label">点位图标</label>
        <div className="poi-editor__icons">
          {POI_ICON_TYPES.map((it) => (
            <button
              key={it.key}
              type="button"
              className={`poi-editor__icon ${
                presetMatch && presetMatch.key === it.key ? 'is-active' : ''
              }`}
              onClick={() => setIconUrl(it.iconUrl)}
            >
              <img src={it.iconUrl} alt="" />
              <span>{it.label}</span>
            </button>
          ))}
          <Upload
            accept="image/*"
            showUploadList={false}
            beforeUpload={handleUpload}
          >
            <button
              type="button"
              className={`poi-editor__icon poi-editor__icon--custom ${
                isCustomIcon ? 'is-active' : ''
              }`}
            >
              {uploading ? (
                <LoadingOutlined />
              ) : isCustomIcon ? (
                <img src={iconUrl} alt="" />
              ) : (
                <PlusOutlined />
              )}
              <span>自定义</span>
            </button>
          </Upload>
        </div>
      </div>

      <div className="poi-editor__field">
        <label className="poi-editor__label poi-editor__label--required">
          位置
        </label>
        <AutoComplete
          className="poi-editor__search"
          value={searchText}
          placeholder="搜索地点，或在地图上点选"
          onChange={(v) => {
            const text = String(v ?? '')
            setSearchText(text)
            search(text)
          }}
          onSelect={(v) => handleSelectResult(String(v))}
          dataSource={results.map((r, i) => (
            <AutoComplete.Option key={String(i)} value={String(i)}>
              <div className="poi-editor__opt">
                <EnvironmentOutlined /> {r.name}
              </div>
            </AutoComplete.Option>
          ))}
        />

        <div className="poi-editor__locrow">
          <PoiPreviewMap lng={lng} lat={lat} onPick={handlePickOnMap} />
          <div className="poi-editor__coords">
            <div className="poi-editor__coords-hint">
              经纬度（搜索/点选后自动填）
            </div>
            <label className="poi-editor__coords-label">纬度</label>
            <InputNumber
              className="poi-editor__coords-input"
              value={lat}
              precision={6}
              placeholder="纬度"
              onChange={(v) => setLat(typeof v === 'number' ? v : undefined)}
            />
            <label className="poi-editor__coords-label">经度</label>
            <InputNumber
              className="poi-editor__coords-input"
              value={lng}
              precision={6}
              placeholder="经度"
              onChange={(v) => setLng(typeof v === 'number' ? v : undefined)}
            />
          </div>
        </div>
      </div>

      <div className="poi-editor__footer">
        <Button onClick={onCancel}>取消</Button>
        <Button type="primary" loading={saving} onClick={handleSave}>
          保存
        </Button>
      </div>
    </div>
  )
}

export default PoiEditor
